import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { stripeSyncedStatusAllowsNexusMainApp } from '../lib/subscription';
import BasicLoadingScreen from './BasicLoadingScreen';

interface SubscriptionGuardProps {
  children: React.ReactNode;
}

/**
 * SubscriptionGuard — allows the main app for active/trialing, and past_due only during
 * the first 4 inclusive calendar days of delinquency; day 5+ redirects to billing.
 */
export default function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const location = useLocation();
  const { selectedBusiness, loading: businessLoading } = useBusiness();
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null);

  useEffect(() => {
    // Still resolving which business this session belongs to
    if (businessLoading) {
      setHasActiveSubscription(null);
      return;
    }

    // Business load finished with nothing selected — stop spinning so AppContent
    // can show the connection/error UI instead of "Checking subscription..." forever.
    if (!selectedBusiness) {
      setHasActiveSubscription(null);
      return;
    }

    const isAllowed = stripeSyncedStatusAllowsNexusMainApp(
      selectedBusiness.subscription_status as string | undefined,
      selectedBusiness.subscription_past_due_at,
    );

    console.log('🔒 SubscriptionGuard: Checking subscription status', {
      businessId: selectedBusiness.id,
      subscriptionStatus: selectedBusiness.subscription_status,
      activePlanId: selectedBusiness.active_plan_id,
      allowsMainApp: isAllowed
    });

    setHasActiveSubscription(isAllowed);
  }, [selectedBusiness, businessLoading]);

  if (businessLoading) {
    return <BasicLoadingScreen message="Checking subscription..." />;
  }

  // Let AppContent render its connection-error / retry UI
  if (!selectedBusiness) {
    return <>{children}</>;
  }

  if (hasActiveSubscription === null) {
    return <BasicLoadingScreen message="Checking subscription..." />;
  }

  if (!hasActiveSubscription) {
    console.log('❌ SubscriptionGuard: Subscription not in an allowed Stripe-synced state, redirecting to subscription management');
    const pastDueHardLock =
      String(selectedBusiness.subscription_status || '')
        .trim()
        .toLowerCase() === 'past_due';

    return (
      <Navigate
        to="/admin-subscription-management"
        replace
        state={{
          from: location.pathname,
          ...(pastDueHardLock ? { fromPastDueLock: true } : {}),
        }}
      />
    );
  }

  console.log('✅ SubscriptionGuard: Allowed Stripe-synced subscription status, rendering app');
  return <>{children}</>;
}
